import { FC, ReactNode, useState } from 'react';
import { captureException } from '@sentry/react';
import { BiDuplicate } from 'react-icons/bi';
import { TbEdit, TbLink, TbTrash } from 'react-icons/tb';

import { useResourceActionAccess } from '@/hooks/useResourceActionAccess';
import { useWorkspaceResourceCreateAccess } from '@/hooks/useWorkspaceResourceCreateAccess';
import RenameResourceModal from '@/shared/components/modals/ResourceActionModals/01-RenameResourceModal';
import DeleteResourceModal from '@/shared/components/modals/ResourceActionModals/03-DeleteResourceModal';
import { toast } from '@/shared/components/toasts/PlatformToast';
import {
    getWorkspaceResourceStatusToggleLabel,
    WorkspaceResourceStatusActionIcon,
} from '@/shared/components/workspace-resources';
import { useTeamStore } from '@/stores';
import { Team } from '@/types';

import { WorkspaceResourceActionMenuItem } from '../helpers';
import WorkspaceResourceActionsDropdown from '../WorkspaceResourceActionsDropdown';

interface TeamsActionsDropdownProps {
    team: Team;
    children: ReactNode;
}

const TeamsActionsDropdown: FC<TeamsActionsDropdownProps> = ({ team, children }): JSX.Element => {
    // const [templatized, setTemplatized] = useState(team?.isTemplate ? team?.isTemplate : false);
    // const [transferVisible, setTransferVisible] = useState(false);

    const [renameVisible, setRenameVisible] = useState(false);
    const [deleteVisible, setDeleteVisible] = useState(false);

    const { teams, toggleTeamArchive, duplicateTeam, toggleTeamLock } = useTeamStore();
    const createAccess = useWorkspaceResourceCreateAccess('teams', teams?.length ?? 0);
    const readAccess = useResourceActionAccess('teams', 'read', team?.id);
    const updateAccess = useResourceActionAccess('teams', 'update', team?.id);
    const manageAccess = useResourceActionAccess('teams', 'manage', team?.id);

    const handleToggleLock = async () => {
        toggleTeamLock(team?.id, !team?.locked);
    };

    const handleToggleArchive = async () => {
        toggleTeamArchive(team?.id, !team?.archived);
    };

    const handleDuplicateTeam = async () => {
        if (createAccess.disabled) {
            return;
        }

        duplicateTeam(team?.id);
    };

    const items: WorkspaceResourceActionMenuItem[] = [
        {
            key: 'locked',
            icon: <WorkspaceResourceStatusActionIcon active={team?.locked} status='locked' />,
            label: getWorkspaceResourceStatusToggleLabel('locked', team?.locked),
            onClick: handleToggleLock,
            permission: 'manage',
        },
        {
            key: 'archived',
            icon: <WorkspaceResourceStatusActionIcon active={team?.archived} status='archived' />,
            label: getWorkspaceResourceStatusToggleLabel('archived', team?.archived),
            onClick: handleToggleArchive,
            permission: 'manage',
        },
        {
            key: 'copyLink',
            icon: <TbLink size={'1rem'} />,
            label: 'Copy Link',
            onClick: () => {
                navigator.clipboard.writeText(window.location.href).then(
                    () => {
                        toast('Link Copied', {
                            icon: <TbLink size='1.125rem' style={{ marginRight: '0.5rem' }} />,
                            description: team?.name,
                        });
                    },
                    (error) => {
                        toast('Error Copying Link', {
                            description: team?.name,
                        });

                        captureException(error);
                    },
                );
            },
            permission: 'read',
        },
        {
            key: 'rename',
            icon: <TbEdit size={'1rem'} />,
            label: 'Rename',
            onClick: () => {
                setRenameVisible(true);
            },
            permission: 'update',
        },
        {
            key: 'duplicate',
            disabled: createAccess.disabled,
            icon: <BiDuplicate size={'1rem'} />,
            label: 'Duplicate',
            onClick: handleDuplicateTeam,
            permission: 'read',
        },
        // {
        //     key: 'templatized',
        //     icon: templatized ? <TbIcons size={'1rem'} /> : <TbIconsOff size={'1rem'} />,
        //     label: templatized ? 'Revert Template' : 'Convert to Template',
        //     onClick: () => {
        //         authorizedAxiosInstance
        //             .put(`/api/teams/${team?.id}`, {
        //                 isTemplate: !templatized,
        //             })
        //             .then((res: AxiosResponse) => {
        //                 if (res.status === 200) {
        //                     setArchived(res?.data?.isTemplate);

        //                     toast(`Investment ${archived ? 'Template Reverted' : 'Converted to Template'}`, {
        //                         icon: res?.data?.isTemplate ? <TbIconsOff size={'1rem'} /> : <TbIcons size={'1rem'} />,
        //                         description: res?.data?.editedName,
        //                     });
        //                 }
        //             });
        //     },
        //     disabled: true,
        // },
        // {
        //     key: 'export',
        //     icon: <TbFileExport size={'1rem'} style={{ transform: 'rotate(-90deg)' }} />,
        //     label: 'Export',
        //     onClick: () => {
        //         return;
        //     },
        //     disabled: true,
        // },
        // {
        //     key: 'settings',
        //     icon: <TbSettings size={'1rem'} />,
        //     label: 'Settings',
        //     onClick: () => {
        //     },
        // },
        {
            key: 'delete',
            icon: <TbTrash size={'1rem'} />,
            label: 'Delete',
            onClick: () => {
                setDeleteVisible(true);
            },
            danger: true,
            permission: 'manage',
        },
    ];

    return (
        <>
            <WorkspaceResourceActionsDropdown
                access={{ manage: manageAccess, read: readAccess, update: updateAccess }}
                history={{ resourceId: team.id, resourceType: 'teams', title: team.name }}
                items={items}
                resourceLabel='Team'
            >
                {children}
            </WorkspaceResourceActionsDropdown>
            {/* Modals */}
            <RenameResourceModal
                open={renameVisible}
                setOpen={setRenameVisible}
                resource={team}
                singularResource='team'
            />
            <DeleteResourceModal
                open={deleteVisible}
                setOpen={setDeleteVisible}
                resource={team}
                singularResource='team'
            />
        </>
    );
};

export default TeamsActionsDropdown;
